import React, { Component } from 'react'
import { connect } from "react-redux"
import * as d3 from "d3"

import { setDragging } from "template-edit/reducers"

import {
    updateInputValue as change,
    selectFormValues,
} from "app/reducers/form";

import { setActiveGroupId, setActiveShapeId, setActiveView } from "shape-edit/reducers"
import { selectLayers, toggleSelectLayer } from "shape-list/reducers"

import ShapeType, { BRUSH_TYPE, NO_TEXT_CURSOR_TYPES, RATING_TYPE, RESIZE_TYPES_TO_CHANGE_FONTSIZE } from "shape-entities/types/shape"
import { ACTIVE_VIEW_EDIT_SHAPE, ACTIVE_VIEW_SHAPE_SELECTION, ActiveViewType } from "template-edit/containers/Editor"
import { createGroupFromSelected, getCursorByType,  getFormName, getScaledShapeSizeAndAngle, getShapeSize, getTemplateSize, GroupSizeAndPosition } from 'shape-edit/forms/helpers';
import { Gradient, SizeType } from "app/types/model-types";
import { getShapeByType, MIN_FONT_SIZE } from "template-edit/components/helper";
import { isPointShapeType } from "../defaults";
import { RandomProduct } from 'shape-edit/types';
import { DeltaPositionsType, initDeltaPositions, POINTER_CURSOR } from 'template-edit/containers/image-preview';

import { find } from 'lodash'
import { scaleBrushStrokes, scalePoints } from 'shape-edit/forms/delta-positions-helper';
import { LevelsModeType } from 'template-edit/components/save-template-form';
import { TemplateOrAlternativeTemplateType } from 'template-edit/undoRedoReducers';
import { EditModeType } from 'app/types/app-types';
import { setEditMode } from 'actions';
import { SELECT_ALL_TEXT_EVENT } from 'shape-entities/render-text';

interface Props {
    data: ShapeType
    levels: string[]
    levelsMode: LevelsModeType
    activeView: ActiveViewType
    activeShapeId: string
    activeGroupId: string
    size: SizeType
    isAlt: boolean
    editMode: EditModeType
    form: TemplateOrAlternativeTemplateType
    randomProduct: RandomProduct
    selectedLayers: string[]
    lockedLayers: string[]
    setDragging: typeof setDragging
    change: typeof change
    toggleSelectLayer: typeof toggleSelectLayer
    selectLayers: typeof selectLayers
    setActiveShapeId: typeof setActiveShapeId
    setActiveGroupId: typeof setActiveGroupId
    setActiveView: typeof setActiveView
    setEditMode: typeof setEditMode
    groupDeltaPositions: DeltaPositionsType
    groupDeltaAngle: number
    deltaAngle: number
    deltaPositions: DeltaPositionsType
    calculatedGroupSizeAndPosition: GroupSizeAndPosition
    setGroupDeltaPositions: Function
    setgroupDeltaAngle: Function
    setDeltaAngle: Function
    setDeltaPositions: Function
    updateGroupedShapesInStore: Function
    gradients: Gradient[]
    setMouseOverShape: Function
    handleRotate: Function
    handleResize: Function
    onDragStarted?: Function
}

export type CoordsType = {
    x: number
    y: number
}

export type DirectionType = {
    dx: number
    dy: number
}
export type CoordsWithMiddleType = {
    topLeft: CoordsType
    bottomRight: CoordsType
    middle: CoordsType
    allRotatedPoints?: CoordsType[]
    angle: number
}

interface State {
    dragStartX: number
    dragStartY: number
    dragScaleX: number
    dragScaleY: number
    isDiffShape: boolean
    isTextDrag: boolean
}

const { abs, sin, cos, PI, tan, atan2, round, min, max } = Math

class Shape extends Component<Props, State> {
    shapeRef: React.RefObject<SVGGElement>
    //true once a real (mouse-moved) drag has forced editMode to 'select' for the current drag gesture - see dragStarted/dragged
    dragModeSwitched: boolean

    constructor(props) {
        super(props)

        this.state = {
            dragStartX: 0,
            dragStartY: 0,
            dragScaleX: 1,
            dragScaleY: 1,
            isDiffShape: null,
            isTextDrag: false,
        };

        this.shapeRef = React.createRef()
        this.dragModeSwitched = false
    }

    isMultiselect = () : boolean => {
        return !!(this.props.selectedLayers && this.props.selectedLayers.length > 1)
    }

    //RenderText owns the text selection as local state, so it can't be set from here directly - notify it via
    //a document event instead (RenderText listens for it in componentDidMount)
    selectAllShapeText = (index: string) => {
        document.dispatchEvent(new CustomEvent(SELECT_ALL_TEXT_EVENT, { detail: { index } }))
    }

    selectShape = (event) => {
        const { activeShapeId, data, lockedLayers, activeView, form, editMode } = this.props

        let isSelected = false
        const isLocked = lockedLayers.includes(data.index)
        const isPointType = isPointShapeType(data.type)

        if (activeView === ACTIVE_VIEW_EDIT_SHAPE) {
            if (isPointType) { //do not select/unselect shape, it is a point type shape edited....
                return;
            }

            const parentNode = event.target?.parentNode
            if (parentNode && parentNode.classList.contains('editable-text-active')) {  //do not select/unselect shape, it is an editable text shape
                return;
            }
        }

        if (event.ctrlKey) {
            //is shape in group? if part of the group? select all, otherwise select only one index:
            let isInGroup = false
            if (form.groups && Array.isArray(form.groups)) {
                form.groups.forEach(group => {
                    if (group.list.includes(data.index)) {
                        group.list.forEach(index => {
                            this.props.toggleSelectLayer(index)
                        })
                        isInGroup = true
                    }
                })
            }

            if (!isInGroup) {
                this.props.toggleSelectLayer(data.index)
            }

            if (activeShapeId) {
                this.props.toggleSelectLayer(activeShapeId)  //add active into group too
            }

            this.props.setActiveShapeId(null)
            this.props.setActiveGroupId(null)
            this.props.setActiveView(ACTIVE_VIEW_SHAPE_SELECTION)
            this.props.setGroupDeltaPositions({...initDeltaPositions})
            this.props.setgroupDeltaAngle(0)
        } else {
            //check if it is a part of a group:
            if (form.groups && Array.isArray(form.groups)) {
                form.groups.forEach(group => {
                    if (group.list.includes(data.index)) {
                        this.props.selectLayers(group.list)
                        this.props.setActiveShapeId(null)
                        this.props.setActiveGroupId(group.index)
                        isSelected = true
                        return
                    }
                })
            }

            //not part of a group, select single shape
            if (!isSelected /* && !isLocked */) {   //we can select even locked shape
                event.preventDefault()
                const isDiffView = activeView !== ACTIVE_VIEW_EDIT_SHAPE
                this.props.setActiveView(!isLocked && (isDiffView || this.state.isDiffShape) ? ACTIVE_VIEW_EDIT_SHAPE : ACTIVE_VIEW_SHAPE_SELECTION)
                this.props.selectLayers([]) //on click without ctrl deselect all from multiselect
                this.props.setActiveShapeId(data.index)
                this.props.setActiveGroupId(null)

                //editMode is only switched by a genuine click (this handler), never by dragging - see dragStarted
                const isTextTarget = event.target?.tagName !== 'rect'

                if (NO_TEXT_CURSOR_TYPES.includes(data.type)) {
                    this.props.setEditMode('select')
                } else if (isTextTarget) {
                    this.props.setEditMode('text')

                    if (event.detail === 2 && editMode === 'text') {   //double click while already text-editing this shape - select all
                        this.selectAllShapeText(data.index)
                    }
                } else {
                    console.debug(`something else, current editMode=${editMode}, setting to 'select' mode`)
                    this.props.setEditMode('select')
                }
            }
        }
    }

    dragStarted = (event) => {
        const { activeShapeId, selectedLayers, data, form, editMode } = this.props

        const isTextTarget = event?.sourceEvent?.target?.tagName !== 'rect'
        //was this shape already being text-edited before this mousedown? if so, a drag starting on the text is a
        //text-selection drag, not a request to move the shape
        const wasAlreadyTextEditing = editMode === 'text' && activeShapeId === data.index

        if (isTextTarget && wasAlreadyTextEditing) {
            this.setState({ isTextDrag: true })
            return
        }

        this.setState({ isTextDrag: false })
        this.dragModeSwitched = false

        if (event.sourceEvent.ctrlKey || this.props.lockedLayers.includes(data.index)) {
            return;
        }

        const templateSize = getTemplateSize(form)

        this.props.setDragging(true)
        this.props.setDeltaPositions({ ...initDeltaPositions, index: data.index, type: null })  //move or null
        this.props.setGroupDeltaPositions({ ...initDeltaPositions })

        if (typeof this.props.onDragStarted === 'function') {
            this.props.onDragStarted()
        }

        const wrapper = document.getElementById('preview-background-canvas')
        const boundingClientRect = wrapper.getBoundingClientRect()

        const dragScaleX = boundingClientRect?.width ? templateSize.width / boundingClientRect.width : 1
        const dragScaleY = boundingClientRect?.height ? templateSize.height / boundingClientRect.height : 1
        const isDiffShape = data.index !== activeShapeId

        let isSelected = false
        if (isDiffShape) {
            if (selectedLayers && selectedLayers.includes(data.index)) {   //is in selected layers?
                isSelected = true
            } else if (form.groups && Array.isArray(form.groups)) {     //is it part of a group?
                form.groups.forEach(group => {
                    if (group.list.includes(data.index)) {
                        this.props.selectLayers(group.list)
                        this.props.setActiveShapeId(null)
                        this.props.setActiveGroupId(group.index)
                        isSelected = true
                        return
                    }
                })
            }
        }

        //not part of a group, just single object dragged?
        if (!isSelected) {
            this.props.setActiveShapeId(data.index)
            this.props.setActiveGroupId(null)
            this.props.selectLayers()
        }

        const angle = (data.angle || 0)
        const el = this.shapeRef.current

        d3.select(el)
            .raise()
            .classed("active", true)
            .style("cursor", getCursorByType('move', angle))

        this.setState({
            dragStartX: event.sourceEvent.clientX,
            dragStartY: event.sourceEvent.clientY,
            dragScaleX: dragScaleX,
            dragScaleY: dragScaleY,
            isDiffShape: isDiffShape,
        })
    }

    rotateByAngle = (coords: CoordsType, angle: number = 0): CoordsType => {
        const alphaRadians = -angle / 180 * PI

        const c = cos(alphaRadians)
        const s = sin(alphaRadians)

        const A = coords.x
        const B = coords.y

        return {
            x: A*c - B*s,
            y: A*s + B*c,
        }
    }

    dragged = (event) => {
        const { calculatedGroupSizeAndPosition, data } = this.props

        if (this.state.isTextDrag) {
            return;
        }

        //the mouse has actually moved now, so this is a genuine drag, not a click - safe to leave text edit mode
        //(deferred until here, rather than dragStarted's mousedown, so a plain click never dispatches this and
        //risks a re-render swapping out the DOM node before the browser's native click event can fire on it)
        if (!this.dragModeSwitched) {
            this.dragModeSwitched = true
            this.props.setEditMode('select')
        }

        if (this.props.lockedLayers.includes(data.index)) {
            return;
        }

        const isMultiSelect = this.isMultiselect()
        const x = round(event.sourceEvent.clientX - this.state.dragStartX) * this.state.dragScaleX
        const y = round(event.sourceEvent.clientY - this.state.dragStartY) * this.state.dragScaleY

        const rotatedPoint = this.rotateByAngle({ x: x, y: y }, isMultiSelect ? calculatedGroupSizeAndPosition.groupAngle : data.angle || 0)

        const dx = rotatedPoint.x
        const dy = rotatedPoint.y

        //Move pos. If we are near the guideline, then correct the transition
        if (isMultiSelect) {
            this.props.setGroupDeltaPositions({
                x1: dx,
                x2: dx,
                y1: dy,
                y2: dy,
                type: null, //or move?
            })
        } else {
            this.props.setDeltaPositions({
                x1: dx,
                x2: dx,
                y1: dy,
                y2: dy,
                index: data.index,
                type: null, //or move?
            })
        }
    }

    dragEnded = (event) => {
        if (this.state.isTextDrag) {
            this.setState({ isTextDrag: false })
            return;
        }

        if (this.props.lockedLayers.includes(this.props.data.index)) {
            return;
        }

        const isMultiSelect = this.isMultiselect()

        this.props.setDragging(false);
        d3.select(this.shapeRef.current).classed("active", false)

        //moving more layers?
        if (isMultiSelect) {
            this.props.updateGroupedShapesInStore()
        } else {
            this.props.handleResize()
            this.props.setGroupDeltaPositions({ ...initDeltaPositions })  //is this really needed?
            this.props.setActiveShapeId(this.props.data.index)
        }
    }

    handleRightClick = (event: PointerEvent) => {
        const { selectedLayers, activeGroupId, form, isAlt } = this.props
        event.preventDefault();

        if (selectedLayers.length > 0) {
            const formName = getFormName(isAlt)

            if (activeGroupId === null) {
                //group
                createGroupFromSelected()
            } else {
                //ungroup
                if (form.groups) {
                    const group = form.groups.find((g) => g.index === activeGroupId);
                    const newGroups = form.groups.filter(g => g.index !== activeGroupId)
                    this.props.selectLayers(group.list)
                    this.props.setActiveGroupId(null)
                    this.props.change(formName, 'groups', newGroups)
                }
            }
        } else {
            //unselect all
            this.props.setActiveShapeId(null)
            this.props.setActiveGroupId(null)
            this.props.setActiveView(ACTIVE_VIEW_SHAPE_SELECTION)
        }
    }

    handleMouseOver = (event, index) => {
        this.props.setMouseOverShape(index)
    }

    handleMouseOut = (event, index) => {
        this.props.setMouseOverShape(null)
    }

    bindShapeEvents = () => {
        const shape = this.props.data
        if (shape.is_draggable) {
            const el = this.shapeRef.current
            if (el && d3.select(el)) {
                const dragBehavior = d3.drag()
                const defaultDragFilter = dragBehavior.filter()

                d3.select(el)
                    .style("cursor", POINTER_CURSOR)
                    .filter((event) => !this.props.lockedLayers.includes(shape.index))
                    .on("click", (event) => this.selectShape(event))
                    .on("contextmenu", (event) => this.handleRightClick(event))
                    .on("mouseover", (event) => this.handleMouseOver(event, shape.index))
                    .on("mouseout", (event) => this.handleMouseOut(event, shape.index))
                    .call(
                        dragBehavior
                            //d3-drag calls event.stopImmediatePropagation() on mousedown as soon as a gesture starts,
                            //before "start"/dragStarted ever runs - too late for dragStarted's own wasAlreadyTextEditing
                            //check to help, since that stops the native mousedown from ever reaching the DOM node
                            //React's handleTextMouseDown is delegated to. Keep the gesture from starting at all here so
                            //the event bubbles normally to the text handlers instead.
                            .filter((event) => {
                                const isTextTarget = event.target?.tagName !== 'rect'
                                const wasAlreadyTextEditing = this.props.editMode === 'text' && this.props.activeShapeId === this.props.data.index
                                return defaultDragFilter.call(this, event) && !(isTextTarget && wasAlreadyTextEditing)
                            })
                            .on("start", (event) => this.dragStarted(event))
                            .on("drag", (event) => this.dragged(event))
                            .on("end", (event) => this.dragEnded(event))
                    )
            }
        }
    }

    componentDidMount() {
        this.bindShapeEvents()
    }

    updateShape = (updatedShape: ShapeType): void => {
        const { isAlt } = this.props
        const formName = getFormName(isAlt)

        this.props.change(formName, `shapes.${updatedShape.index}`, updatedShape)
    }

    render() {
        const { data, gradients, size, activeShapeId, selectedLayers, calculatedGroupSizeAndPosition,
            groupDeltaPositions, deltaPositions, lockedLayers, levels, levelsMode, editMode } = this.props

        const isMultiSelect = this.isMultiselect()
        const isSelected: boolean = selectedLayers.length > 0 ? selectedLayers.includes(data.index) : data.index === activeShapeId
        const isLocked: boolean = lockedLayers.length > 0 ? lockedLayers.includes(data.index) : false
        const isDraggable: boolean = data.is_draggable
        const isAspectRatio: boolean = data.is_aspect_size_ratio

        const { width, height } = getShapeSize(data)

        const groupDeltaWidth = isMultiSelect && groupDeltaPositions ? (groupDeltaPositions.x2 - groupDeltaPositions.x1) : 0
        const groupDeltaHeight = isMultiSelect && groupDeltaPositions ? (groupDeltaPositions.y2 - groupDeltaPositions.y1) : 0

        const shapeDeltaWidth = !isMultiSelect && deltaPositions ? (deltaPositions.x2 - deltaPositions.x1) : 0
        const shapeDeltaHeight = !isMultiSelect && deltaPositions ? (deltaPositions.y2 - deltaPositions.y1) : 0

        const groupScaleX = isMultiSelect && calculatedGroupSizeAndPosition?.groupWidth ? (calculatedGroupSizeAndPosition.groupWidth + groupDeltaWidth) / calculatedGroupSizeAndPosition.groupWidth : 1
        const groupScaleY = isMultiSelect && calculatedGroupSizeAndPosition?.groupHeight ? (calculatedGroupSizeAndPosition.groupHeight + groupDeltaHeight) / calculatedGroupSizeAndPosition.groupHeight : 1

        const shapeScaleX = (width + shapeDeltaWidth) / width       //pokud neni veskupine, jinak je potreba invertrotatedshapesize pouzit a z toho urcit scaleX
        const shapeScaleY = (height + shapeDeltaHeight) / height

        const groupScaleAvg = (groupScaleX + groupScaleY) / 2

        const oldGroupX1 = calculatedGroupSizeAndPosition?.middleX - calculatedGroupSizeAndPosition?.groupWidth / 2
        const oldGroupY1 = calculatedGroupSizeAndPosition?.middleY - calculatedGroupSizeAndPosition?.groupHeight / 2

        const newGroupX1 = oldGroupX1 + groupDeltaPositions.x1
        const newGroupY1 = oldGroupY1 + groupDeltaPositions.y1

        const scaleX = (isAspectRatio ? groupScaleAvg : groupScaleX) * shapeScaleX
        const scaleY = (isAspectRatio ? groupScaleAvg : groupScaleY) * shapeScaleY

        const coefX = data.is_flop ? -1 : 1
        const coefY = data.is_flip ? -1 : 1

        const scaledSize = getScaledShapeSizeAndAngle(width, height, scaleX, scaleY, data.angle || 0)

        const scaledWidth = scaledSize.width
        const scaledHeight = scaledSize.height

        const translateReflection = data.reflection_flip ? - data.reflection_offset : (height + data.reflection_offset)

        let deltaXpos = 0
        let deltaYpos = 0
        let deltaWidth = 0
        let deltaHeight = 0

        if (isSelected && isDraggable) {
            if (isMultiSelect) {
                deltaWidth = scaledWidth - width
                deltaHeight = scaledHeight - height

                deltaXpos = round((data.x - oldGroupX1) * groupScaleX + newGroupX1 - data.x)
                deltaYpos = round((data.y - oldGroupY1) * groupScaleY + newGroupY1 - data.y)

                const alphaRadians = -(data.angle || 0) / 180 * PI
                const betaRadians = (calculatedGroupSizeAndPosition.groupAngle || 0) / 180 * PI

                const c = cos(alphaRadians)
                const s = sin(alphaRadians)

                const cc = cos(betaRadians)
                const ss = sin(betaRadians)

                const groupDeltaXRot = deltaXpos * c - deltaYpos * s
                const groupDeltaYRot = deltaXpos * s + deltaYpos * c

                deltaXpos = groupDeltaXRot * cc - groupDeltaYRot * ss
                deltaYpos = groupDeltaXRot * ss + groupDeltaYRot * cc
            } else if (deltaPositions) {
                deltaWidth = deltaPositions.x2 - deltaPositions.x1
                deltaHeight = deltaPositions.y2 - deltaPositions.y1

                deltaXpos = coefX*(deltaPositions.x1 + deltaWidth / 2)
                deltaYpos = coefY*(deltaPositions.y1 + deltaHeight / 2)
            }
        }

        const currentGradient = data.gradient_id && gradients && Array.isArray(gradients) ? find(gradients, (gradient: Gradient) => {
            return gradient.id === data.gradient_id
        }) : null

        const avgScale = (shapeScaleX + shapeScaleY) / 2

        let fontSizeCoef = 1
        if (isSelected && width && data.text_fontsize && data.text_fontsize * avgScale > MIN_FONT_SIZE) {
            if (data.type === RATING_TYPE || RESIZE_TYPES_TO_CHANGE_FONTSIZE.includes(deltaPositions?.type)) {   //or isAspectRatio && handle type je rohovy!!!
                fontSizeCoef = avgScale
            }
        }

        const shapeWithRelations = {
            ...data,
            gradient: currentGradient,
            points: isSelected && data.points ? scalePoints(data.points, shapeScaleX * groupScaleX, shapeScaleY * groupScaleY) : data.points,
            brush_strokes: isSelected && data.brush_strokes ? scaleBrushStrokes(data.brush_strokes, shapeScaleX * groupScaleX, shapeScaleY * groupScaleY) : data.brush_strokes,
            text_fontsize: isMultiSelect && isSelected && data.text_fontsize ? abs(round(data.text_fontsize * groupScaleAvg)) : data.text_fontsize*fontSizeCoef,
            angle: scaledSize.angle,    //debug this!!!!!
        }

        const shapeWithRelationsTranslated = {
            ...shapeWithRelations,
            width: width + deltaWidth,
            height: height + deltaHeight,
            text_fontsize: isSelected ? data.text_fontsize*fontSizeCoef : data.text_fontsize,
        }

        const clipPathUrl = data.clip_mask ? `url(#shape_clip_mask_${data.index})` : undefined
        const sourceXORFilterPath = data.clip_mask ? `url(#shape_xor_me_${data.index})` : undefined
        const noPointerEvents = /* isLocked || */ (data.type === BRUSH_TYPE && activeShapeId === data.index)
        const reflectionAngle = round(data.reflection_angle || 0)

        return (
            <React.Fragment>
                {data.has_reflection &&
                    <React.Fragment>
                        <defs>
                            <linearGradient id={`gradient-${data.index}`} y1="0%" y2="100%">
                                <stop offset="0%" stopColor="black" stopOpacity=".9" />
                                <stop offset="100%" stopColor="white" stopOpacity="1" />
                            </linearGradient>
                            <mask id={`gradient-mask-${data.index}`}>
                                <rect x={-width / 2} y={-height / 2} width={width} height={height} fill={`url(#gradient-${data.index})`} />
                            </mask>
                        </defs>
                        <g
                            className={'shape-reflection'}
                            transform={`translate(${deltaXpos}, ${translateReflection+deltaYpos}), scale(1,${data.reflection_flip ? 1 : -1}), skewX(${reflectionAngle}), translate(${-round(tan(PI * reflectionAngle / 180) * .5 * height)},0)`}
                            mask={`url(#gradient-mask-${data.index})`}
                            style={{
                                opacity: 1 - (data.reflection_opacity / 100),
                                pointerEvents: isLocked ? 'none' : 'auto',
                            }}
                        >
                            {data.invert_clip_mask ?
                                <g mask={sourceXORFilterPath}>
                                    {getShapeByType(shapeWithRelationsTranslated, size, false, activeShapeId, editMode)}
                                </g> :
                                <g clipPath={clipPathUrl}>
                                    {getShapeByType(shapeWithRelationsTranslated, size, false, activeShapeId, editMode)}
                                </g>
                            }
                        </g>
                    </React.Fragment>
                }
                <g ref={isDraggable ? this.shapeRef : undefined} transform={`translate(${deltaXpos},${deltaYpos})`} style={{ pointerEvents: noPointerEvents ? 'none' : 'auto' }}>
                    {data.invert_clip_mask ?
                        <g mask={sourceXORFilterPath}>
                            {getShapeByType(shapeWithRelations, size, true, activeShapeId, editMode, this.updateShape, levels, levelsMode, deltaWidth, deltaHeight)}
                        </g> :
                        <g clipPath={clipPathUrl}>
                            {getShapeByType(shapeWithRelations, size, true, activeShapeId, editMode, this.updateShape, levels, levelsMode, deltaWidth, deltaHeight)}
                        </g>
                    }
                </g>
            </React.Fragment>
        )
    }
}

function mapStateToProps(state) {
    return {
        isAlt: state.isAlt,
        editMode: state.editMode,
        activeShapeId: state.activeShapeId,
        activeGroupId: state.activeGroupId,
        randomProduct: state.randomProduct,
        activeView: state.activeView,
        selectedLayers: state.selectedLayers,
        lockedLayers: state.lockedLayers,
        gradients: state.gradients,
        form: selectFormValues(state, getFormName(state.isAlt)),
    }
}

export default connect(mapStateToProps, {
    setEditMode,
    setDragging,
    setActiveView,
    setActiveShapeId,
    setActiveGroupId,
    toggleSelectLayer,
    selectLayers,
    change,
})(Shape)
